/**
 * Additional security checks that can be integrated into the assessments
 */

export const additionalSecurityChecks = {
  // Common checks for all providers
  common: [
    {
      category: "API Security",
      checks: [
        { title: "API Gateway authentication", description: "All API endpoints require strong authentication", risk: "high" },
        { title: "API rate limiting", description: "Rate limits prevent abuse and DoS attacks", risk: "medium" },
        { title: "API monitoring and logging", description: "API activity is logged and monitored for anomalies", risk: "medium" }
      ]
    },
    {
      category: "Serverless Security",
      checks: [
        { title: "Serverless function permissions", description: "Least privilege principle applied to serverless functions", risk: "high" },
        { title: "Serverless function dependencies", description: "Dependencies scanned for vulnerabilities", risk: "medium" },
        { title: "Serverless function timeouts", description: "Functions configured with appropriate timeouts", risk: "low" }
      ]
    },
    {
      category: "Container Security",
      checks: [
        { title: "Container image scanning", description: "Images scanned for vulnerabilities before deployment", risk: "high" },
        { title: "Container runtime security", description: "Runtime protection and monitoring enabled", risk: "high" },
        { title: "Container networking policies", description: "Network segmentation between containers", risk: "medium" },
        { title: "Container registry security", description: "Registry requires authentication and image signing", risk: "high" }
      ]
    },
    {
      category: "Cost & Resource Management",
      checks: [
        { title: "Resource tagging strategy", description: "Consistent tagging for cost attribution and security", risk: "medium" },
        { title: "Budget alerts configured", description: "Alerts for unusual spending patterns", risk: "low" },
        { title: "Resource lifecycle policies", description: "Automated cleanup of unused resources", risk: "low" },
        { title: "Access controls for cost management", description: "Limited access to create high-cost resources", risk: "medium" }
      ]
    },
    {
      category: "Data Sovereignty",
      checks: [
        { title: "Data residency compliance", description: "Data stored in compliant regions", risk: "high" },
        { title: "Cross-region data transfer controls", description: "Controls on moving data between regions", risk: "medium" },
        { title: "Geo-fencing policies", description: "Access restricted based on geographic location", risk: "medium" }
      ]
    },
    {
      category: "Advanced Threat Protection",
      checks: [
        { title: "AI/ML-based threat detection", description: "Advanced threat detection using machine learning", risk: "medium" },
        { title: "User behavior analytics", description: "Detection of anomalous user behaviors", risk: "medium" },
        { title: "Advanced DDoS protection", description: "Layer 7 application-aware DDoS mitigation", risk: "high" }
      ]
    }
  ],

  // AWS-specific checks
  aws: [
    {
      category: "Quantum-Safe Security",
      checks: [
        { title: "Post-quantum cryptography readiness", description: "Plan for transitioning to quantum-resistant algorithms", risk: "medium" },
        { title: "KMS hybrid post-quantum TLS", description: "Enabled post-quantum key exchange algorithms", risk: "low" }
      ]
    },
    {
      category: "AWS-Specific Protections",
      checks: [
        { title: "AWS Shield Advanced enabled", description: "Advanced DDoS protection activated", risk: "medium" },
        { title: "AWS Control Tower implemented", description: "Centralized governance across multiple accounts", risk: "medium" },
        { title: "AWS Macie for sensitive data", description: "Automated sensitive data discovery and protection", risk: "high" },
        { title: "AWS Backup compliance", description: "Centralized backup strategy meeting compliance needs", risk: "medium" }
      ]
    }
  ],

  // Azure-specific checks
  azure: [
    {
      category: "Azure-Specific Protections",
      checks: [
        { title: "Azure Confidential Computing", description: "Data-in-use encryption for sensitive workloads", risk: "medium" },
        { title: "Azure Landing Zones implemented", description: "Well-architected framework deployment", risk: "medium" },
        { title: "Azure Purview for data governance", description: "Data classification and governance solution", risk: "medium" },
        { title: "Azure AD Privileged Identity Management", description: "Just-in-time and just-enough-access principles", risk: "high" }
      ]
    }
  ],

  // GCP-specific checks
  gcp: [
    {
      category: "GCP-Specific Protections",
      checks: [
        { title: "VPC Service Controls enabled", description: "Data exfiltration prevention for GCP services", risk: "high" },
        { title: "Sensitive Data Protection", description: "DLP controls for sensitive information", risk: "high" },
        { title: "Shielded VMs enabled", description: "Secure boot and vTPM for virtual machines", risk: "medium" },
        { title: "Binary Authorization for GKE", description: "Deploy only trusted container images", risk: "high" }
      ]
    }
  ],

  // Multi-cloud specific checks
  multi: [
    {
      category: "Multi-Cloud Orchestration",
      checks: [
        { title: "Unified security policy management", description: "Consistent policies across cloud providers", risk: "high" },
        { title: "Cross-cloud monitoring", description: "Centralized visibility across environments", risk: "high" },
        { title: "Unified identity solution", description: "Single identity source across clouds", risk: "high" },
        { title: "Cloud-agnostic DR planning", description: "Disaster recovery across providers", risk: "medium" },
        { title: "Multi-cloud network security", description: "Consistent network controls across clouds", risk: "high" }
      ]
    }
  ]
};

// Guide for integrating these checks into the main assessment
export const integrationNotes = `
To integrate these additional checks:

1. Import this file in assessments.js
2. Add the common checks to all providers
3. Add provider-specific checks to the corresponding provider
4. Update the UI components if necessary to handle the new categories

Example integration code:
import { additionalSecurityChecks } from './additionalChecks';

// Add common checks to all providers
Object.keys(assessmentsData).forEach(provider => {
  additionalSecurityChecks.common.forEach(category => {
    assessmentsData[provider].categories.push(category);
  });
});

// Add provider-specific checks
Object.keys(additionalSecurityChecks).forEach(provider => {
  if (provider !== 'common' && assessmentsData[provider]) {
    additionalSecurityChecks[provider].forEach(category => {
      assessmentsData[provider].categories.push(category);
    });
  }
});
`; 